/**
 * Section — THE STANDARD MODULE. Every block on every surface is one.
 *
 * Ported from the Atlas Universe showroom (`src/frames/operator/Section`),
 * pass 2 of the component recipe (COMPONENT-RECIPE.md §5). It is the piece
 * that makes "a new brand works on day one" true rather than hopeful: a
 * surface is a LIST OF SECTIONS, not hand-assembled markup, so a brand swap
 * cannot leave one block styled and another not — there is only one block,
 * used many times.
 *
 * Three shapes, and no fourth without an ADR:
 *   `stack` — cards down the page with `--card-gap` (the round's articles)
 *   `rail`  — cards across, horizontally scrollable (videos, audio)
 *   `bleed` — one element edge to edge, no gutter at all (the hero)
 *
 * The header is delegated to `SectionHeader` — one component per concept
 * (ADR-059). A Section without a title renders no header at all and takes
 * its accessible name from `label`.
 *
 * A STRUCTURAL primitive like `Surface`, `SectionHeader` and `Chip`:
 * deliberately not in `PRIMITIVE_METAS` / the manifest — the LLM
 * layout-planner composes content blocks, never chrome (ADR-060).
 */

import type { ReactElement, ReactNode } from 'react';
import { SectionHeader } from './SectionHeader.js';
import styles from './Section.module.css';

export type SectionShape = 'stack' | 'rail' | 'bleed';

export interface SectionProps {
  /** Visible title. Omit for an unheaded block (then set `label`). */
  title?: ReactNode;
  /** Quiet trailing number beside the title — "6", "12 markeder". */
  count?: ReactNode;
  /** Right-aligned affordance ("se alle"). Only render one that leads somewhere. */
  action?: ReactNode;
  shape?: SectionShape;
  /** Accessible name when there is no visible title. */
  label?: string;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function Section({
  title,
  count,
  action,
  shape = 'stack',
  label,
  children,
  className,
  'data-testid': testId,
}: SectionProps): ReactElement {
  const ariaLabel = label ?? (typeof title === 'string' ? title : undefined);
  return (
    <section
      className={[styles.section, className ?? ''].filter(Boolean).join(' ')}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      data-testid={testId}
    >
      {title != null ? (
        <SectionHeader
          title={title}
          {...(count != null ? { count } : {})}
          {...(action != null ? { action } : {})}
        />
      ) : null}
      <div className={styles[shape]}>{children}</div>
    </section>
  );
}

export default Section;
